import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import "./Questions.css";
import {
  postGetStoryAnswer,
  postGenerateBook,
  postGetOptimizedTranscript,
  postCheckCouponForPayment,
  postCheckSubscriptionCoupon,
  postCheckAudiobookQuota,
  getUser,
  postUpdateBookAttribute,
  postUpdateKeyCharacters,
} from "../../../../../core/api/Api";
import { useParams, useLocation } from "react-router-dom";
import {
  checkIfChapterHaveLessThanOneQuestion,
  showHideModal,
  updateElementClassById,
  showToast,
  getDateInTimeZone,
  getInputSpeechId,
} from "../../../../../core/common-service/CommonService";
import Loading from "../../../../common/loading/Loading";
import LoadingCreateBook from "../../../../common/loading/LoadingCreateBook";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { StoreContext } from "../../../../../core/context/StoreContext";
import LockedBookModal from "../../../../_components/Modal";
import useIsMobile from "../../../../../core/hooks/useMobile";
import useLangNavigate from "../../../../common/useLangNavigate";
import { getCurrencyForLocale } from "../../../../../config/locales";

// Import custom hooks and components
import { QuestionsContext } from "./context/QuestionsContext";
import { useQuestionsData } from "./hooks/useQuestionsData";
import { useImageUpload } from "./hooks/useImageUpload";
import { useDragDrop } from "./hooks/useDragDrop";
import AccordionSection from "./components/AccordionSection";
import ChapterItem from "./components/ChapterItem";
import BookHeader from "./components/BookHeader";
import ActionButtons from "./components/ActionButtons";
import BookPreviewModal from "./components/BookPreviewModal";
import FloatingPreviewButton from "./components/FloatingPreviewButton";
import DownloadBookModal from "./components/DownloadBookModal";
import TrustpilotModal from "./components/TrustpilotModal";
import {
  AddChapterMaxReachedModal,
  ConfirmAnswersModal,
  UpsertChapterModal,
  DeleteChapterModal,
  DeleteQuestionModal,
  UpsertQuestionModal,
} from "./components/ModalComponents";
import SingleChapterView from "./components/SingleChapterView";
import KeyCharactersPanel from "./components/KeyCharactersPanel";

const Questions = () => {
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const { isLoggedIn, user } = useContext(StoreContext);
  let { service_session_id } = useParams();
  const location = useLocation();
  const navigate = useLangNavigate();
  const { t, i18n } = useTranslation();
  const currency = getCurrencyForLocale(i18n.language);

  // Modal IDs
  const modal_add_chapter_max_reached_id = "modal_add_chapter_max_reached_id";
  const modal_choose_plan_id = "modal_choose_plan_id";
  const modal_confirm_answers_id = "modal_confirm_answers_id";
  const modal_upsert_chapter_id = "modal_upsert_chapter_id";
  const modal_delete_chapter_id = "modal_delete_chapter_id";
  const modal_delete_question_id = "modal_delete_question_id";
  const modal_upsert_question_id = "modal_upsert_question_id";
  const modal_submit_chapter_changes_id = "modal_submit_chapter_changes_id";
  const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
  const [chapterPhotosUrls, setChapterPhotosUrls] = useState({});
  const [isPreparingAudiobook, setIsPreparingAudiobook] = useState(false);

  // Custom hooks
  const {
    checkpoint_data,
    setCheckpointData,
    data,
    setData,
    synopsis,
    setSynopsis,
    introduction,
    setIntroduction,
    summary,
    setSummary,
    bookTitle,
    setBookTitle,
    bookType,
    survey_questions,
    setSurveyQuestions,
    services_with_plans,
    general_category_id,
    category_based_type_id,
    category_length_type_id,
    keyCharacters,
    setKeyCharacters,
    loading,
    setLoading,
    isPaid,
    setIsPaid,
    progressBarPercentage,
    mounted,
    updateSessionData,
    getData,
    getPaymentForm,
    updateCircularProgressBar,
  } = useQuestionsData(service_session_id, isLoggedIn);
  const {
    uploadedImageForChapterFormData,
    uploadImagesArr,
    coverPhotoInputId,
    coverPhotoImageId,
    otherPhotoInputId,
    otherPhotoImageId,
    onUploadImageClick,
    onUploadImageChange,
    onRemoveImage,
    loadCachedImages,
    onGenerateWithAI,
    onGenerateChapterImageWithAI,
    generatingChapterId,
    appendCachedImagesToFormData,
  } = useImageUpload(
    service_session_id,
    setCoverPhotoUrl,
    setChapterPhotosUrls,
  );

  const {
    dragStartChapter,
    dragEnterChapter,
    dropChapter,
    dragStartQuestion,
    dragEnterQuestion,
    dropQuestion,
  } = useDragDrop();

  // Local state
  const [editIntro, setEditIntro] = useState(false);
  const [editSynopsis, setEditSynopsis] = useState(false);
  const [editSummary, setEditSummary] = useState(false);
  const [code, setCode] = useState(user?.coupon_code ?? "");
  const [activeCollapse, setActiveCollapse] = useState("flush-collapse-");
  const [chapterToUpsert, setChapterToUpsert] = useState({});
  const [questionToUpsert, setQuestionToUpsert] = useState({});
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showTrustpilotModal, setShowTrustpilotModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // Default open on desktop, closed on mobile (detect synchronously so it
  // doesn't flash open before useIsMobile resolves on first paint).
  const [isCharactersSidebarOpen, setIsCharactersSidebarOpen] = useState(() => {
    if (typeof navigator === "undefined") return true;
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileDevice =
      /android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    return !isMobileDevice;
  });

  // Refs
  const chapterToUpsertResource = useRef({
    type: null,
    index: null,
    item: null,
  });
  const questionToUpsertResource = useRef({
    type: null,
    question_category_index: null,
    question_index: null,
  });

  const { chapter_to_add, index_chapter_to_add } = location.state || {};

  // New inline chapter handlers
  const handleUpdateChapter = (chapterData, chapterIndex) => {
    const tmp_data = [...data];
    tmp_data[chapterIndex] = chapterData;
    setData(tmp_data);
  };

  const handleAddChapter = (chapterData, afterIndex) => {
    const tmp_data = [...data];
    tmp_data.splice(afterIndex + 1, 0, chapterData);
    setData(tmp_data);
  };

  // New inline question handlers
  const handleUpdateQuestion = (chapterIndex, questionIndex, questionData) => {
    const tmp_data = [...data];
    tmp_data[chapterIndex].questions[questionIndex] = {
      ...questionData,
      isNewQuestion: false,
    };
    setData(tmp_data);
  };

  const handleAddQuestion = (chapterIndex, questionIndex) => {
    const tmp_data = [...data];
    const newQuestion = {
      id: Date.now(), // Generate unique ID
      name: "", // Empty name for new questions
      transcript: "",
      question_category_id: tmp_data[chapterIndex].id,
      category_based_type_id,
      category_length_type_id,
      isNewQuestion: true, // This is the key flag!
    };

    // Insert after the specified questionIndex
    tmp_data[chapterIndex].questions.splice(questionIndex + 1, 0, newQuestion);
    setData(tmp_data);
  };

  const addExtraChapterPlan = [
    {
      id: 1,
      service_id: null,
      category_length_type_id: null,
      price: 4.99,
      currency_id: 1,
      is_gift_card: false,
      is_active: true,
      name: t("extra_chapter"),
      description: null,
      payed_per: null,
      price_description: t("for_the_chapter"),
      plan_options: [
        {
          id: 10,
          plan_id: 3,
          is_active: true,
          name: t("add_chapter_plans.reached_the_maximum_number"),
          description: null,
        },
        {
          id: 11,
          plan_id: 3,
          is_active: true,
          name: t("add_chapter_plans.cost_every_additional_chapter", { currency_name: currency.name }),
          description: null,
        },
      ],
      currency: {
        id: 1,
        symbol: currency.symbol,
        is_active: true,
        name: currency.name,
        description: null,
      },
    },
  ];

  const handleAccordion = (e) => {
    let attr = e.target.attributes?.name?.nodeValue;

    if (!attr) {
      const parent = e.target.parentElement;
      if (parent && parent.attributes?.name) {
        attr = parent.attributes.name.nodeValue;
      } else {
        return;
      }
    }

    let currentValue = attr?.split("-")?.at(-1);

    if (
      parseInt(currentValue) === 0 ||
      currentValue === "synopsis" ||
      currentValue === "introduction" ||
      currentValue === "summary" ||
      isPaid
    ) {
      setActiveCollapse(activeCollapse === attr ? false : attr);
      setIsPayModalOpen(false);
      return;
    }

    setActiveCollapse(activeCollapse === attr ? false : attr);
    setIsPayModalOpen(activeCollapse !== attr);
  };

  // Toggle edit mode for a section; entering edit mode also expands its accordion
  const toggleSectionEdit = (sectionId, isEditing, setEditing) => () => {
    setEditing(!isEditing);
    if (!isEditing) setActiveCollapse(sectionId);
  };

  const onEditAddDeleteChapter = (type, index) => (e) => {
    e.stopPropagation();
    if (type === "delete") {
      const tmp_data = [...data];
      tmp_data.splice(index, 1);
      setData(tmp_data);
      uploadImagesArr.forEach((element, image_index) => {
        const id = "chap_" + data[index].id + "_media_" + image_index;
        uploadedImageForChapterFormData.current.delete(id);
      });
      return;
    }

    chapterToUpsertResource.current = { type, index };
    showHideModal(modal_upsert_chapter_id);

    switch (type) {
      case "edit":
        setChapterToUpsert(data[index]);
        break;
      case "add":
        const tmp_data = [...data];
        const nextId = Math.max(...tmp_data.map((item) => item.id)) + 1;
        setChapterToUpsert({
          id: nextId,
          general_category_id,
          name: "",
          questions: [],
        });
        break;
      default:
        break;
    }
  };

  const onChapterToUpsertChange = (type, chap_question_index) => (e) => {
    e.preventDefault();
    const value = e.target.value;
    var tmp_chapterToUpsert = { ...chapterToUpsert };
    switch (type) {
      case "chapter-change-text":
        tmp_chapterToUpsert.name = value;
        break;
      case "question-change-text":
        tmp_chapterToUpsert.questions[chap_question_index].name = value;
        break;
      case "question-delete-text":
        if (tmp_chapterToUpsert?.questions?.length > 1) {
          tmp_chapterToUpsert?.questions.splice(chap_question_index, 1);
        }
        break;
      case "question-add-text":
        tmp_chapterToUpsert.questions.push({
          name: "",
          question_category_id: tmp_chapterToUpsert?.id,
          category_based_type_id,
          category_length_type_id,
        });
        break;
      default:
        break;
    }
    setChapterToUpsert(tmp_chapterToUpsert);
  };

  const onSubmitChapterToUpsert = () => {
    if (chapterToUpsert?.name?.length > 0) {
      if (chapterToUpsert?.questions?.length > 0) {
        showHideModal(modal_upsert_chapter_id);
        const tmp_data = [...data];
        if (chapterToUpsertResource.current.type === "edit") {
          tmp_data[chapterToUpsertResource.current.index] = chapterToUpsert;
        } else if (chapterToUpsertResource.current.type === "add") {
          tmp_data.splice(
            chapterToUpsertResource.current.index + 1,
            0,
            chapterToUpsert,
          );
        }
        if (!checkIfChapterHaveLessThanOneQuestion(tmp_data)) {
          setData(tmp_data);
        }
      } else {
        showToast({
          type: "error",
          message: t("please_add_questions_in_the_chapter"),
        });
      }
    } else {
      showToast({ type: "error", message: t("please_add_chapter_title") });
    }
  };

  function splitStringByLineBreaks(inputString) {
    const regex = /\r?\n/g;
    const matches = [...inputString.matchAll(regex)];
    const result = [];

    let lastIndex = 0;

    matches.forEach((match) => {
      const textSegment = inputString.slice(lastIndex, match.index).trim();

      if (textSegment) {
        result.push({ text: textSegment });
      }

      lastIndex = match.index + match[0].length;
    });

    const remainingText = inputString.slice(lastIndex).trim();
    if (remainingText) {
      result.push({ text: remainingText });
    }

    return result;
  }

  const onChangeTextArea = (index) => (e) => {
    var tmp_data = [...data];
    tmp_data[index].generated_content = e.target.value;

    const arrayOfObjects = splitStringByLineBreaks(
      tmp_data[index].generated_content,
    );

    tmp_data[index].generated_content_json = JSON.stringify(arrayOfObjects);

    setData(tmp_data);
  };

  const onClickGenerateAudiobook = async () => {
    setIsPreparingAudiobook(true);

    try {
      const formData = new FormData();
      formData.append("service_session_id", service_session_id);
      formData.append("return_file", "true");

      const response = await postGenerateBook(formData, true);

      if (response?.data) {
        const contentDisposition = response.headers["content-disposition"];
        const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
        const docxName = filenameMatch
          ? filenameMatch[1]
          : `${service_session_id}_generated_document.docx`;

        const docxFile = new File([response.data], docxName, {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        navigate("/audiobook-upload", "", {
          state: {
            preloadedFile: docxFile,
            fileName: docxName,
            bookTitle: bookTitle,
            fromBookGeneration: true,
            service_session_id: service_session_id,
          },
        });
      }
    } catch (error) {
      showToast({
        type: "error",
        message: t("audiobook.preparation_error") + ": " + error.message,
      });
    } finally {
      setIsPreparingAudiobook(false);
    }
  };

  const onClickGetPrintedBook = async () => {
    try {
      const formData = new FormData();
      formData.append("service_session_id", service_session_id);
      formData.append("return_file", "true");

      // Append cached images (cover + chapter photos) so the docx includes them
      appendCachedImagesToFormData(formData, data);
      const addedKeys = new Set();
      for (const [key] of formData.entries()) {
        addedKeys.add(key);
      }
      for (const [
        key,
        value,
      ] of uploadedImageForChapterFormData.current.entries()) {
        if (!addedKeys.has(key)) {
          formData.append(key, value);
          addedKeys.add(key);
        }
      }

      const response = await postGenerateBook(formData, true);

      if (response?.data) {
        const contentDisposition = response.headers["content-disposition"];
        const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
        const docxName = filenameMatch
          ? filenameMatch[1]
          : `${service_session_id}_generated_document.docx`;

        const docxFile = new File([response.data], docxName, {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        navigate("/printing", "", {
          state: {
            preloadedFile: docxFile,
            fileName: docxName,
            bookTitle: bookTitle,
            fromBookGeneration: true,
            service_session_id: service_session_id,
          },
        });
      }
    } catch (error) {
      showToast({
        type: "error",
        message: t("printing.preparation_error") + ": " + error.message,
      });
    }
  };

  const onClickUpdateTitle = (title) => {
    const requestData = {
      id: service_session_id,
      attribute: "book_name",
      value: title,
    };
    postUpdateBookAttribute(requestData);
  };

  const onClickWriteStory = () => {
    if (!isLoggedIn) {
      setIsPayModalOpen(true);
      return;
    }

    if (!isPaid) return;

    setShowDownloadModal(true);
  };

  const handleDownloadConfirm = () => {
    setShowDownloadModal(false);
    retrieveBook();
  };
  const retrieveBook = () => {
    const formData = new FormData();
    formData.append("service_session_id", service_session_id);

    // Load images from localStorage
    appendCachedImagesToFormData(formData, data);
    const addedKeys = new Set(["service_session_id"]);
    for (const [key] of formData.entries()) {
      addedKeys.add(key);
    }
    for (const [
      key,
      value,
    ] of uploadedImageForChapterFormData.current.entries()) {
      if (!addedKeys.has(key)) {
        formData.append(key, value);
        addedKeys.add(key);
      }
    }

    setLoading(true);
    postGenerateBook(formData)
      .then((res) => {
        if (mounted.current && res?.data?.success) {
          setLoading(false);
          const response = res.data?.success;
          if (response?.url && response?.name) {
            saveAs(response.url, response.name);
          }

          if (response?.epub_url && response?.epub_name) {
            saveAs(response.epub_url, response.epub_name);
          }

          setShowTrustpilotModal(true);
        }
      })
      .catch((e) => {
        setLoading(false);
      });
  };

  const onCancelWriteStory = () => {
    showHideModal(modal_confirm_answers_id);
  };

  const onClickOpenDeleteChapterModal = (index) => (e) => {
    e.stopPropagation();
    showHideModal(modal_delete_chapter_id);
    chapterToUpsertResource.current = { type: "delete", index };
  };

  const onClickDeleteChapter = (e) => {
    onEditAddDeleteChapter("delete", chapterToUpsertResource.current.index)(e);
    showHideModal(modal_delete_chapter_id);
    chapterToUpsertResource.current = { type: null, index: null };
  };

  const onEditDeleteQuestion = (
    event,
    type,
    question_category_index,
    question_index,
  ) => {
    event.preventDefault();
    questionToUpsertResource.current = {
      type,
      question_category_index,
      question_index,
    };
    switch (type) {
      case "edit":
        setQuestionToUpsert(
          data[question_category_index].questions[question_index],
        );
        showHideModal(modal_upsert_question_id);
        break;
      case "delete":
        showHideModal(modal_delete_question_id);
        break;
      case "optimize":
        const question_transcript =
          data?.[question_category_index]?.questions[question_index]
            ?.transcript ?? null;
        if (question_transcript?.length > 0) {
          setLoading(true);
          const optimize_icon_id =
            getInputSpeechId(
              data[question_category_index].questions[question_index],
            ) + "_optimize_icon";
          updateElementClassById(optimize_icon_id, "fa-fade", "add");
          postGetOptimizedTranscript({ transcript: question_transcript })
            .then((res) => {
              setLoading(false);
              updateElementClassById(optimize_icon_id, "fa-fade", "remove");
              const response = res?.data?.success ?? null;
              if (response) {
                const tmp_data = [...data];
                tmp_data[question_category_index].questions[
                  question_index
                ].transcript = response?.content;
                setData(tmp_data);

                const question =
                  data?.[question_category_index]?.questions[question_index];
                document.getElementById(getInputSpeechId(question)).value =
                  response?.content;
              }
            })
            .catch((error) => {
              setLoading(false);
              updateElementClassById(optimize_icon_id, "fa-fade", "remove");
            });
        }
        break;
      default:
        break;
    }
  };

  const onClickDeleteQuestion = (e) => {
    e.preventDefault();
    const tmp_data = [...data];
    if (
      tmp_data[questionToUpsertResource.current.question_category_index]
        .questions.length > 1
    ) {
      tmp_data[
        questionToUpsertResource.current.question_category_index
      ].questions.splice(questionToUpsertResource.current.question_index, 1);
      setData(tmp_data);
    } else {
      showToast({
        type: "warning",
        message: t("at_lest_one_question_is_required"),
      });
    }

    showHideModal(modal_delete_question_id);
    questionToUpsertResource.current = {
      type: null,
      question_category_index: null,
      question_index: null,
    };
  };

  const onQuestionToUpsertChange = (e) => {
    e.preventDefault();
    const value = e.target.value;
    setQuestionToUpsert((prevState) => ({
      ...prevState,
      name: value,
    }));
  };

  const onClickEditAddQuestion = (e) => {
    e.preventDefault();
    const objResource = questionToUpsertResource.current;
    var tmp_data = [...data];
    switch (objResource.type) {
      case "edit":
        tmp_data[objResource.question_category_index].questions[
          objResource.question_index
        ].name = questionToUpsert.name;
        setData(tmp_data);
        showHideModal(modal_upsert_question_id);
        break;

      default:
        break;
    }
  };

  // UPDATED: Support custom answers - now adds them as permanent options
  const onSurveyAnswerClick =
    (index, answer_index, isCustom = false, customText = null) =>
    (e) => {
      e.preventDefault();
      var tmp_survey_questions = [...survey_questions];

      if (isCustom) {
        // User is entering/updating a custom answer
        if (customText && customText.trim()) {
          // Check if this custom text already exists in survey_answers
          const existingCustom = tmp_survey_questions[
            index
          ].survey_answers.find(
            (ans) => ans.is_custom && ans.name === customText.trim(),
          );

          if (!existingCustom) {
            // Add new custom answer to the list
            const newCustomAnswer = {
              id: null, // null ID indicates it's a custom answer for backend
              name: customText.trim(),
              is_selected: true,
              is_custom: true, // Flag to identify custom answers
            };

            // Deselect all others and add new custom answer
            tmp_survey_questions[index].survey_answers.forEach((element) => {
              element.is_selected = false;
            });
            tmp_survey_questions[index].survey_answers.push(newCustomAnswer);
          } else {
            // Select the existing custom answer
            tmp_survey_questions[index].survey_answers.forEach((element) => {
              element.is_selected = element === existingCustom;
            });
          }
        }
        // Keep track that custom input is active (for UI state)
        tmp_survey_questions[index].is_custom_selected =
          !customText || !customText.trim();
        tmp_survey_questions[index].custom_answer = customText || "";
      } else {
        // User selected a predefined or previously saved custom answer
        tmp_survey_questions[index].survey_answers.forEach((element, i) => {
          element.is_selected = answer_index === i;
        });
        tmp_survey_questions[index].is_custom_selected = false;
        tmp_survey_questions[index].custom_answer = null;
      }

      setSurveyQuestions(tmp_survey_questions);
    };

  // UPDATED: Check both predefined and custom answers
  const checkAllSurveyAnswersSelected = (data) => {
    return data.every((question) => {
      // Check if either a predefined answer is selected OR custom answer is filled
      const hasPredefinedSelection = question.survey_answers.find(
        (answer) => answer.is_selected,
      );
      const hasCustomSelection =
        question.is_custom_selected &&
        question.custom_answer &&
        question.custom_answer.trim().length > 0;

      return hasPredefinedSelection !== undefined || hasCustomSelection;
    });
  };

  const onResult = (index, q_index, transcript_str) => {
    let tmp_data = [...data];
    tmp_data[index].questions[q_index].transcript = transcript_str;
    setData(tmp_data);
    updateCircularProgressBar(tmp_data);
  };

  const onChooseAddChapterPlanClick = () => {
    const obj = {
      service_session_id,
      is_add_chapter: true,
      chapter_to_add: chapterToUpsert,
      index_chapter_to_add: chapterToUpsertResource.current.index,
    };
    navigate("/payment", "", { state: { ...obj } });
  };

  const onSubmitChapterClick = (item, index) => (e) => {
    e.preventDefault();
    chapterToUpsertResource.current = { item, index };
    onSubmit(e);
  };

  // UPDATED: Handle custom answers from survey_questions state
  const onSubmit = (e) => {
    e.preventDefault();
    const index = chapterToUpsertResource.current.index;

    if (!isPaid && index > 0) {
      showHideModal(modal_choose_plan_id);
      return;
    }

    if (checkAllSurveyAnswersSelected(survey_questions)) {
      // Build selected_survey_questions - handle both custom and predefined answers
      const selected_survey_questions = survey_questions.map((item) => {
        // Find the selected answer (could be predefined or custom)
        const selectedAnswer = item.survey_answers.find(
          (q_item) => q_item?.is_selected,
        );

        // If selected answer is a custom one (has is_custom flag)
        if (selectedAnswer?.is_custom) {
          return {
            service_session_id,
            survey_question_id: item?.id,
            survey_answer_id: null, // null for custom answers
            custom_answer: selectedAnswer.name.trim(),
          };
        } else {
          // Predefined answer from backend
          return {
            service_session_id,
            survey_question_id: item?.id,
            survey_answer_id: selectedAnswer?.id,
            custom_answer: null,
          };
        }
      });

      setLoading(true);
      postGetStoryAnswer({
        data: data[index],
        service_session_id,
        selected_survey_questions,
        index,
        isPaid,
        user,
      })
        .then((res) => {
          setLoading(false);
          const response = res.data?.success;
          let tmp_data = [...data];
          tmp_data[index].submitted_at = getDateInTimeZone();
          tmp_data[index].generated_content = response?.content;
          tmp_data[index].generated_content_json = JSON.stringify(
            response?.content_json,
          );
          if (!isPaid) {
            tmp_data[index].generated_count =
              (tmp_data[index].generated_count ?? 0) + 1;
          }
          setData(tmp_data);
          setSummary(response?.summary);

          // Save survey selections (including custom answers) to checkpoint_data
          const surveySelections = {};
          survey_questions.forEach((question) => {
            const selectedAnswer = question.survey_answers.find(
              (a) => a.is_selected,
            );
            if (selectedAnswer) {
              surveySelections[question.id] = {
                survey_answer_id: selectedAnswer.is_custom
                  ? null
                  : selectedAnswer.id,
                custom_answer: selectedAnswer.is_custom
                  ? selectedAnswer.name
                  : null,
                custom_answers: question.survey_answers
                  .filter((a) => a.is_custom)
                  .map((a) => a.name), // Save all custom answers for this question
              };
            }
          });

          setCheckpointData((prev) => ({
            ...prev,
            last_survey_selections: surveySelections,
          }));

          document.getElementById(`generated_paragraph_${index}`).style.height =
            "auto";

          setTimeout(() => {
            document
              .getElementById(`generated_paragraph_${index}`)
              .scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "start",
              });
          }, [100]);
        })
        .catch((err) => {
          setLoading(false);

          // ✅ Display detailed error from backend
          const errorMessage =
            err?.response?.data?.error || t("error.unknown_error");

          // Show user-friendly error message
          showToast({
            type: "error",
            message: errorMessage,
            duration: 8000, // Longer duration for detailed error messages
          });
        });
      chapterToUpsertResource.current = {};
    } else {
      showToast({
        type: "error",
        message: t("error.please_answer_all_questions"),
      });
    }
  };
  const onChoosePlanClick = (item) => (e) => {
    if (e?.isSubscription) {
      navigate("/subscriptions", "", { state: { couponCode: code.trim() } });
    } else {
      const plan_id = item?.plans[0]?.id;
      const obj = {
        service_session_id,
        plan_id: plan_id,
      };
      navigate("/payment", "", { state: { ...obj } });
    }
    return;
  };

  const onCodeChange = (e) => {
    const val = e.target.value;
    setCode(val);
  };

  const checkCouponForPayment = ({ isSubscription } = {}) => {
    if (isSubscription) {
      if (!code.trim()) {
        showToast({ type: "error", message: t("please_enter_coupon") });
        return;
      }
      postCheckSubscriptionCoupon({ code: code.trim() })
        .then((res) => {
          if (mounted.current && res?.data?.success) {
            showToast({ type: "success", message: t("coupon_valid") });
          }
        })
        .catch((error) => {
          showToast({
            type: "error",
            message: error?.response?.data?.error || t("invalid_coupon"),
          });
        });
      return;
    }

    const plan_id = services_with_plans[0].plans[0].id;
    postCheckCouponForPayment({ plan_id, service_session_id, code })
      .then((res) => {
        if (mounted.current && res?.data?.success) {
          setLoading(false);
          const response = res.data?.success;
          setIsPaid(response?.is_paid);
          if (response?.is_paid) {
            showHideModal(modal_choose_plan_id);
          } else {
            showToast({
              type: "success",
              message: t("coupon_successful"),
            });
          }
        }
      })
      .catch(() => {
        showToast({ type: "error", message: t("error.unknown_error") });
      });
  };

  const saveIntroduction = async () => {
    try {
      const data = {
        id: service_session_id,
        attribute: "introduction",
        value: introduction,
      };

      await postUpdateBookAttribute(data);
      showToast({
        type: "success",
        message: t("introduction_updated_successfully"),
      });
      setEditIntro(false);
    } catch (error) {
      showToast({
        type: "error",
        message: t("failed_to_update_introduction"),
      });
    }
  };

  const saveSynopsis = async () => {
    try {
      const data = {
        id: service_session_id,
        attribute: "another_story_subject",
        value: synopsis,
      };

      await postUpdateBookAttribute(data);
      showToast({
        type: "success",
        message: t("synopsis_updated_successfully"),
      });
      setEditSynopsis(false);
    } catch (error) {
      showToast({
        type: "error",
        message: t("failed_to_update_synopsis"),
      });
    }
  };

  const saveSummary = async () => {
    try {
      const data = {
        id: service_session_id,
        attribute: "book_summary",
        value: summary,
      };

      await postUpdateBookAttribute(data);
      showToast({
        type: "success",
        message: t("summary_updated_successfully"),
      });
      setEditSummary(false);
    } catch (error) {
      showToast({
        type: "error",
        message: t("failed_to_update_summary"),
      });
    }
  };

  const saveKeyCharacters = async (updatedCharacters) => {
    setKeyCharacters(updatedCharacters);
    try {
      await postUpdateKeyCharacters({
        id: service_session_id,
        key_characters: updatedCharacters,
      });
      showToast({ type: "success", message: t("characters_updated_successfully") });
    } catch (error) {
      showToast({ type: "error", message: t("failed_to_update_characters") });
    }
  };

  const handlePreviewBook = () => {
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  // Handle drop for chapters
  const handleDropChapter = (e) => {
    const newData = dropChapter(data)(e);
    setData(newData);
  };

  // Handle drop for questions
  const handleDropQuestion = (index) => (e) => {
    const newData = dropQuestion(data, index)(e);
    setData(newData);
  };

  // Effects
  useEffect(() => {
    mounted.current = true;
    updateSessionData();
    loadCachedImages(data);
    return () => {
      mounted.current = false;
    };
  }, [data, loadCachedImages, updateSessionData]);

  useEffect(() => {
    const accrodionBtn = document.getElementsByClassName("accordion-button");
    for (let i = 0; i < accrodionBtn.length; i++) {
      if (accrodionBtn[i].attributes.name.nodeValue === activeCollapse) {
        document
          .getElementById(activeCollapse)
          ?.classList.add("accordion-collapse-show");
        document
          .getElementsByName(activeCollapse)[0]
          ?.classList.add("accordion-button-show");
      } else {
        document
          .getElementById(accrodionBtn[i].attributes.name.nodeValue)
          ?.classList.remove("accordion-collapse-show");
        document
          .getElementsByName(accrodionBtn[i].attributes.name.nodeValue)[0]
          ?.classList.remove("accordion-button-show");
      }
    }
  }, [activeCollapse]);

  useEffect(() => {
    mounted.current = true;
    getData(chapter_to_add, index_chapter_to_add);
    if (isLoggedIn) {
      getPaymentForm();
    }

    if (data?.length > 0) {
      setActiveCollapse("flush-collapse-0");
    }

    window.scrollTo(0, 0);
    return () => {
      mounted.current = false;
    };
  }, [
    getData,
    getPaymentForm,
    isLoggedIn,
    chapter_to_add,
    index_chapter_to_add,
  ]);

  // NEW: Restore survey selections from checkpoint_data (including multiple custom answers)
  useEffect(() => {
    if (
      checkpoint_data?.last_survey_selections &&
      survey_questions.length > 0
    ) {
      const tmp_questions = [...survey_questions];

      tmp_questions.forEach((question) => {
        const saved = checkpoint_data.last_survey_selections[question.id];
        if (saved) {
          // Restore all previously saved custom answers for this question
          if (saved.custom_answers && Array.isArray(saved.custom_answers)) {
            saved.custom_answers.forEach((customText) => {
              // Check if this custom answer already exists
              const exists = question.survey_answers.find(
                (a) => a.is_custom && a.name === customText,
              );
              if (!exists) {
                // Add it back to the survey_answers array
                question.survey_answers.push({
                  id: null,
                  name: customText,
                  is_selected: false,
                  is_custom: true,
                });
              }
            });
          }

          // Restore the selected answer
          if (saved.custom_answer) {
            // A custom answer is selected - mark it
            question.is_custom_selected = false;
            question.custom_answer = null;
            question.survey_answers.forEach((answer) => {
              answer.is_selected =
                answer.is_custom && answer.name === saved.custom_answer;
            });
          } else if (saved.survey_answer_id) {
            // A predefined answer is selected
            question.is_custom_selected = false;
            question.custom_answer = null;
            question.survey_answers.forEach((answer) => {
              answer.is_selected = answer.id === saved.survey_answer_id;
            });
          }
        }
      });

      setSurveyQuestions(tmp_questions);
    }
  }, [checkpoint_data?.last_survey_selections, survey_questions.length]);

  const dragDropHandlers = useMemo(() => ({
    dragStartChapter,
    dragEnterChapter,
    handleDropChapter,
    dragStartQuestion,
    dragEnterQuestion,
    handleDropQuestion,
  }), [dragStartChapter, dragEnterChapter, handleDropChapter, dragStartQuestion, dragEnterQuestion, handleDropQuestion]);

  const imageHandlers = useMemo(() => ({
    uploadImagesArr,
    otherPhotoInputId,
    otherPhotoImageId,
    onUploadImageClick,
    onUploadImageChange,
    onRemoveImage,
    onGenerateChapterImageWithAI,
    generatingChapterId,
  }), [uploadImagesArr, otherPhotoInputId, otherPhotoImageId, onUploadImageClick, onUploadImageChange, onRemoveImage, onGenerateChapterImageWithAI, generatingChapterId]);

  const chapterHandlers = useMemo(() => ({
    onClickOpenDeleteChapterModal,
    onSubmitChapterClick,
    onChangeTextArea,
    onUpdateChapter: handleUpdateChapter,
    onAddChapter: handleAddChapter,
  }), [onClickOpenDeleteChapterModal, onSubmitChapterClick, onChangeTextArea, handleUpdateChapter, handleAddChapter]);

  const questionHandlers = useMemo(() => ({
    onResult,
    onEditDeleteQuestion,
    onUpdateQuestion: handleUpdateQuestion,
    onAddQuestion: handleAddQuestion,
  }), [onResult, onEditDeleteQuestion, handleUpdateQuestion, handleAddQuestion]);

  const isSelfWrite = checkpoint_data?.write_mode === "self";

  const questionsContextValue = useMemo(() => ({
    isPaid,
    general_category_id,
    category_based_type_id,
    category_length_type_id,
    survey_questions,
    onSurveyAnswerClick,
    loading,
    isSelfWrite,
  }), [isPaid, general_category_id, category_based_type_id, category_length_type_id, survey_questions, onSurveyAnswerClick, loading, isSelfWrite]);

  if (data?.length === 0) {
    if (
      checkpoint_data?.correction_status === "pending" ||
      checkpoint_data?.reformulation_status === "pending" ||
      checkpoint_data?.humanization_status === "pending"
    ) {
      return <LoadingCreateBook showTimer={false} />;
    }
    return <Loading />;
  }

  return (
    <QuestionsContext.Provider value={questionsContextValue}>
    <section className="aai-features py-100">
      {/* All Modals */}
      <AddChapterMaxReachedModal
        modalId={modal_add_chapter_max_reached_id}
        addExtraChapterPlan={addExtraChapterPlan}
        onChooseAddChapterPlanClick={onChooseAddChapterPlanClick}
      />

      <ConfirmAnswersModal
        modalId={modal_confirm_answers_id}
        onCancelWriteStory={onCancelWriteStory}
      />

      <UpsertChapterModal
        modalId={modal_upsert_chapter_id}
        chapterToUpsert={chapterToUpsert}
        loading={loading}
        onChapterToUpsertChange={onChapterToUpsertChange}
        onSubmitChapterToUpsert={onSubmitChapterToUpsert}
      />

      <DeleteChapterModal
        modalId={modal_delete_chapter_id}
        onClickDeleteChapter={onClickDeleteChapter}
        showHideModal={showHideModal}
      />

      <DeleteQuestionModal
        modalId={modal_delete_question_id}
        onClickDeleteQuestion={onClickDeleteQuestion}
        showHideModal={showHideModal}
      />

      <UpsertQuestionModal
        modalId={modal_upsert_question_id}
        questionToUpsert={questionToUpsert}
        onQuestionToUpsertChange={onQuestionToUpsertChange}
        onClickEditAddQuestion={onClickEditAddQuestion}
        showHideModal={showHideModal}
      />

      {/* Main Content */}
      <div className="container">
        {/* Book Header with Title and Cover */}
        <BookHeader
          isPaid={isPaid}
          bookTitle={bookTitle}
          bookType={bookType}
          setBookTitle={setBookTitle}
          progressBarPercentage={progressBarPercentage}
          coverPhotoInputId={coverPhotoInputId}
          coverPhotoImageId={coverPhotoImageId}
          onUploadImageClick={onUploadImageClick}
          onUploadImageChange={onUploadImageChange}
          onRemoveImage={onRemoveImage}
          loading={loading || isPreparingAudiobook}
          onClickUpdateTitle={onClickUpdateTitle}
          onClickDownloadBook={onClickWriteStory}
          onClickGenerateAudiobook={onClickGenerateAudiobook}
          onClickGetPrintedBook={onClickGetPrintedBook}
          onClickPreviewBook={handlePreviewBook}
          onGenerateWithAI={onGenerateWithAI}
        />
        {/* Full content area with Characters sidebar */}
        <div className="characters-layout" style={{ marginTop: "1rem" }}>
          {/* Left sidebar — Key Characters */}
          <div className={`characters-sidebar ${isCharactersSidebarOpen ? "characters-sidebar--open" : "characters-sidebar--closed"}`}>
            {isCharactersSidebarOpen ? (
              <KeyCharactersPanel
                keyCharacters={keyCharacters}
                onSave={saveKeyCharacters}
                loading={loading}
                onCollapse={() => setIsCharactersSidebarOpen(false)}
              />
            ) : (
              <button
                className="characters-sidebar-reopen"
                onClick={() => setIsCharactersSidebarOpen(true)}
                title={t("key_characters")}
              >
                <i className="fas fa-users" />
                {keyCharacters.length > 0 && (
                  <span className="characters-sidebar-reopen-count">{keyCharacters.length}</span>
                )}
              </button>
            )}
          </div>

          {/* Right — Synopsis, Introduction, Chapters, Summary */}
          <div className="characters-layout-main">

            {/* Synopsis Section */}
            {synopsis && (
              <AccordionSection
                id="flush-collapse-synopsis"
                title={t("synopsis")}
                content={synopsis}
                handleAccordion={handleAccordion}
                editMode={editSynopsis}
                onEditToggle={toggleSectionEdit(
                  "flush-collapse-synopsis",
                  editSynopsis,
                  setEditSynopsis,
                )}
                onSave={saveSynopsis}
                onContentChange={(e) => setSynopsis(e.target.value)}
              />
            )}

            {/* Introduction Section */}
            {introduction !== " " && (
              <AccordionSection
                id="flush-collapse-introduction"
                title={t("introduction")}
                content={introduction}
                handleAccordion={handleAccordion}
                editMode={editIntro}
                onEditToggle={toggleSectionEdit(
                  "flush-collapse-introduction",
                  editIntro,
                  setEditIntro,
                )}
                onSave={saveIntroduction}
                onContentChange={(e) => setIntroduction(e.target.value)}
                addTopSpacing={!!synopsis}
                saveLabel={t("save_changes")}
              />
            )}

            {/* Chapters */}
            <div className="row justify-content-center" style={{ gap: "1rem", marginTop: "1rem" }}>
              {!checkpoint_data?.is_correction &&
                !checkpoint_data?.is_reformulate &&
                !checkpoint_data?.is_humanize &&
                data?.length > 0 && (
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6c757d",
                      textAlign: "center",
                      width: "100%",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    {t("chapter_titles_editable_hint")}
                  </p>
                )}
              {checkpoint_data?.is_correction || checkpoint_data?.is_reformulate || checkpoint_data?.is_humanize ? (
                <SingleChapterView
                  chapter={data?.[0]}
                  isPaid={isPaid}
                  onPayClick={() => setIsPayModalOpen(true)}
                  onChangeTextArea={onChangeTextArea(0)}
                  bookTitle={bookTitle}
                />
              ) : (
                data?.map((item, index) => (
                  <ChapterItem
                    key={item.id || "question-" + index}
                    item={item}
                    index={index}
                    activeCollapse={activeCollapse}
                    handleAccordion={handleAccordion}
                    dragDropHandlers={dragDropHandlers}
                    imageHandlers={imageHandlers}
                    chapterHandlers={chapterHandlers}
                    questionHandlers={questionHandlers}
                  />
                ))
              )}
            </div>

            {/* Summary Section */}
            {summary !== " " && (
              <AccordionSection
                id="flush-collapse-summary"
                title={t("summary")}
                content={summary}
                handleAccordion={handleAccordion}
                editMode={editSummary}
                onEditToggle={toggleSectionEdit(
                  "flush-collapse-summary",
                  editSummary,
                  setEditSummary,
                )}
                onSave={saveSummary}
                onContentChange={(e) => setSummary(e.target.value)}
                addTopSpacing={true}
                saveLabel={t("save_changes")}
              />
            )}

          </div>
        </div>

        {/* Action Buttons */}
        <ActionButtons
          loading={loading || isPreparingAudiobook}
          isPaid={isPaid}
          onClickDownloadBook={onClickWriteStory}
          onClickGenerateAudiobook={onClickGenerateAudiobook}
          onClickGetPrintedBook={onClickGetPrintedBook}
        />

        <DownloadBookModal
          isOpen={showDownloadModal}
          onConfirm={handleDownloadConfirm}
          onClose={() => setShowDownloadModal(false)}
        />

        <TrustpilotModal
          isOpen={showTrustpilotModal}
          onLeaveReview={() => {
            setShowTrustpilotModal(false);
            navigate("/finished-book-options");
          }}
          onClose={() => {
            setShowTrustpilotModal(false);
            navigate("/finished-book-options");
          }}
        />
      </div>

      {/* Locked Book Modal */}
      {services_with_plans.map((item, index) => (
        <LockedBookModal
          key={index}
          open={isPayModalOpen}
          setOpen={setIsPayModalOpen}
          servicesWithPlans={services_with_plans}
          isPaid={isPaid}
          code={code}
          onCodeChange={onCodeChange}
          checkCouponForPayment={checkCouponForPayment}
          onChoosePlanClick={onChoosePlanClick(item)}
        />
      ))}

      {/* Hide preview button for correction/reformulation books */}
      {!checkpoint_data?.is_correction && !checkpoint_data?.is_reformulate && !checkpoint_data?.is_humanize && (
        <FloatingPreviewButton onClick={handlePreviewBook} disabled={loading} />
      )}

      <BookPreviewModal
        isOpen={showPreviewModal}
        onClose={handleClosePreview}
        bookTitle={bookTitle}
        introduction={introduction}
        synopsis={synopsis}
        summary={summary}
        chapters={data}
        coverPhoto={coverPhotoUrl}
        chapterPhotos={chapterPhotosUrls}
        isPaid={isPaid}
      />

    </section>
    </QuestionsContext.Provider>
  );
};
export default Questions;
